import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import {
  ArrowUp, ArrowDown, FileText, FileSpreadsheet, Printer, Search,
  Layers, ListFilter, X, ChevronDown, CheckSquare
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';

// ---- Constants ----
// Only these statuses are listable
const ALLOWED_STATUSES = ['activa', 'en_taller', 'en revisión', 'rota', 'prestada'];
const STATUS_LABEL = {
  'activa': 'Activa',
  'en_taller': 'En taller',
  'en revisión': 'En revisión',
  'rota': 'Rota',
  'prestada': 'Prestada',
};
const STATUS_COLOR = {
  'activa': 'bg-blue-100 text-blue-700',
  'en_taller': 'bg-orange-100 text-orange-700',
  'en revisión': 'bg-yellow-100 text-yellow-700',
  'rota': 'bg-red-100 text-red-700',
  'prestada': 'bg-purple-100 text-purple-700',
};

// Sort options shown in the toolbar Select
const SORT_OPTIONS = [
  { key: 'number', label: 'Nº bicicleta' },
  { key: 'km', label: 'KM' },
  { key: 'year', label: 'Año' },
  { key: 'model', label: 'Modelo' },
  { key: 'size', label: 'Talla' },
  { key: 'color', label: 'Color' },
  { key: 'status', label: 'Estado' },
];

const GROUPERS = [
  { key: 'year', label: 'Año' },
  { key: 'model', label: 'Modelo' },
  { key: 'color', label: 'Color' },
  { key: 'size', label: 'Talla' },
  { key: 'status', label: 'Estado' },
];

const GROUPER_LABEL = Object.fromEntries(GROUPERS.map(g => [g.key, g.label]));

// ---- Helpers ----
function cciGet(obj, ...names) {
  if (!obj) return '';
  const lookup = names.map(n => n.toLowerCase());
  for (const k of Object.keys(obj)) {
    if (k && lookup.includes(k.trim().toLowerCase())) {
      const v = obj[k];
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
  }
  return '';
}

function getYear(b) {
  if (b.model_year) return b.model_year;
  if (b.purchase_date && typeof b.purchase_date === 'string') {
    const y = parseInt(b.purchase_date.slice(0, 4));
    if (!Number.isNaN(y)) return y;
  }
  return '';
}

function getComponentModel(b) {
  const v = cciGet(b.custom_components, 'modelo');
  return v || b.model || '';
}

function getKm(b) {
  // Prefer custom_components.KM / km / kilómetros, fallback to last_km
  const v = cciGet(b.custom_components, 'km', 'kilómetros', 'kilometros', 'kilometers');
  if (v) {
    const n = parseFloat(v.replace(/\./g, '').replace(',', '.'));
    if (!Number.isNaN(n)) return n;
    return v;
  }
  if (b.last_km != null && b.last_km !== '') return Number(b.last_km);
  return '';
}

function getBattery(b) {
  return cciGet(b.custom_components, 'batería', 'bateria', 'battery', 'bateria nominal');
}

function getGrupo(b) {
  // Primary: top-level bicycle.groupset (Component field 'Grupo' in BicycleDetail)
  if (b.groupset && String(b.groupset).trim() !== '') return String(b.groupset).trim();
  // Fallback: custom_components case-insensitive
  return cciGet(b.custom_components, 'grupo', 'grupo cambio', 'groupset');
}

function formatKmDate(iso) {
  if (!iso || typeof iso !== 'string') return '';
  // accept either '2024-05-12...' or DD/MM/YYYY
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function buildRow(b) {
  return {
    id: b.id,
    number: b.number || '',
    model: getComponentModel(b),
    size: b.size || '',
    color: b.color || '',
    status: b.status || '',
    statusLabel: STATUS_LABEL[b.status] || b.status || '',
    year: getYear(b),
    km: getKm(b),
    km_date: b.last_km_date || '',
    battery: getBattery(b),
    grupo: getGrupo(b),
    frame_number: b.frame_number || '',
    _is_electric: !!b.is_electric,
  };
}

// Compare for sorting
function cmp(a, b, dir) {
  const mul = dir === 'asc' ? 1 : -1;
  if (a === '' && b !== '') return 1; // empty values go to bottom regardless of dir
  if (a !== '' && b === '') return -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * mul;
  return String(a).localeCompare(String(b)) * mul;
}

export default function ExportBicyclesModal({ open, onClose, bicycles, activeEmpresaNombre }) {
  const [search, setSearch] = useState('');
  const [modelFilters, setModelFilters] = useState(() => new Set()); // empty = all models
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState('number');
  const [sortDir, setSortDir] = useState('asc');
  const [groupKeys, setGroupKeys] = useState([]); // ordered list, e.g. ['year','model']
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [modelPopoverOpen, setModelPopoverOpen] = useState(false);
  const [modelSearch, setModelSearch] = useState('');

  // 1) Hard-filter by allowed statuses
  const baseRows = useMemo(
    () => bicycles
      .filter(b => ALLOWED_STATUSES.includes(b.status))
      .map(buildRow),
    [bicycles]
  );

  // 2) Apply filters + search
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return baseRows.filter(r => {
      if (modelFilters.size > 0 && !modelFilters.has(r.model)) return false;
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (!q) return true;
      const text = `${r.number} ${r.model} ${r.size} ${r.color} ${r.frame_number}`.toLowerCase();
      return text.includes(q);
    });
  }, [baseRows, search, modelFilters, statusFilter]);

  // 3) Sort
  const sortedRows = useMemo(() => {
    const copy = [...filteredRows];
    copy.sort((x, y) => cmp(x[sortKey] ?? '', y[sortKey] ?? '', sortDir));
    return copy;
  }, [filteredRows, sortKey, sortDir]);

  // 4) Group (multi-level)
  const groups = useMemo(() => {
    function build(rows, keys) {
      if (keys.length === 0) return [{ labels: [], rows }];
      const map = new Map();
      rows.forEach(r => {
        const k = (r[keys[0]] === '' || r[keys[0]] == null) ? 'Sin valor' : String(r[keys[0]]);
        if (!map.has(k)) map.set(k, []);
        map.get(k).push(r);
      });
      const out = [];
      [...map.entries()]
        .sort(([a], [b]) => String(a).localeCompare(String(b), undefined, { numeric: true }))
        .forEach(([k, subRows]) => {
          const sub = build(subRows, keys.slice(1));
          sub.forEach(sg => out.push({
            labels: [{ key: keys[0], value: k }, ...sg.labels],
            rows: sg.rows,
          }));
        });
      return out;
    }
    if (groupKeys.length === 0) return [{ labels: [], rows: sortedRows }];
    return build(sortedRows, groupKeys);
  }, [sortedRows, groupKeys]);

  // 5) Determine visible columns — hide KM / Batería / Grupo / Color if entirely empty
  const visibleCols = useMemo(() => {
    const has = (key) => sortedRows.some(r => r[key] !== '' && r[key] !== null && r[key] !== undefined);
    return {
      km: has('km'),
      battery: has('battery'),
      grupo: has('grupo'),
      color: has('color'),
    };
  }, [sortedRows]);

  const allModels = useMemo(() => {
    const s = new Set();
    baseRows.forEach(r => r.model && s.add(r.model));
    return [...s].sort();
  }, [baseRows]);

  const availableStatuses = useMemo(() => {
    const s = new Set();
    baseRows.forEach(r => r.status && s.add(r.status));
    return ALLOWED_STATUSES.filter(st => s.has(st));
  }, [baseRows]);

  // ---- Per-row selection ----
  // exportRows: if any selection, only those (intersected with filtered); else all sorted rows
  const exportRows = useMemo(() => {
    if (selectedIds.size === 0) return sortedRows;
    return sortedRows.filter(r => selectedIds.has(r.id));
  }, [sortedRows, selectedIds]);

  // Rebuild groups based on exportRows so that exports honor selection too
  const exportGroups = useMemo(() => {
    function build(rows, keys) {
      if (keys.length === 0) return [{ labels: [], rows }];
      const map = new Map();
      rows.forEach(r => {
        const k = (r[keys[0]] === '' || r[keys[0]] == null) ? 'Sin valor' : String(r[keys[0]]);
        if (!map.has(k)) map.set(k, []);
        map.get(k).push(r);
      });
      const out = [];
      [...map.entries()]
        .sort(([a], [b]) => String(a).localeCompare(String(b), undefined, { numeric: true }))
        .forEach(([k, subRows]) => {
          const sub = build(subRows, keys.slice(1));
          sub.forEach(sg => out.push({
            labels: [{ key: keys[0], value: k }, ...sg.labels],
            rows: sg.rows,
          }));
        });
      return out;
    }
    if (groupKeys.length === 0) return [{ labels: [], rows: exportRows }];
    return build(exportRows, groupKeys);
  }, [exportRows, groupKeys]);

  const toggleRowSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      sortedRows.forEach(r => next.add(r.id));
      return next;
    });
  };
  const clearAllSelection = () => setSelectedIds(new Set());
  // Header checkbox state
  const headerCheckState = useMemo(() => {
    if (sortedRows.length === 0) return false;
    const allSelected = sortedRows.every(r => selectedIds.has(r.id));
    if (allSelected) return true;
    const anySelected = sortedRows.some(r => selectedIds.has(r.id));
    return anySelected ? 'indeterminate' : false;
  }, [sortedRows, selectedIds]);
  const toggleHeaderCheckbox = () => {
    if (headerCheckState === true) {
      // deselect visibles
      setSelectedIds(prev => {
        const next = new Set(prev);
        sortedRows.forEach(r => next.delete(r.id));
        return next;
      });
    } else {
      selectAllVisible();
    }
  };

  // ---- Exports ----
  const buildColumns = () => {
    const cols = [
      { key: 'number', label: 'Nº bicicleta' },
      { key: 'model', label: 'Modelo' },
      { key: 'size', label: 'Talla' },
    ];
    if (visibleCols.color) cols.push({ key: 'color', label: 'Color' });
    cols.push({ key: 'statusLabel', label: 'Estado' });
    cols.push({ key: 'year', label: 'Año compra' });
    if (visibleCols.km) cols.push({ key: 'km', label: 'KM' });
    if (visibleCols.battery) cols.push({ key: 'battery', label: 'Batería' });
    if (visibleCols.grupo) cols.push({ key: 'grupo', label: 'Grupo' });
    return cols;
  };

  const formatCell = (r, key) => {
    const v = r[key];
    if (v == null || v === '') {
      // KM column may still show just a date for the rare case last_km is missing but date exists
      if (key === 'km' && r.km_date) return formatKmDate(r.km_date);
      return '';
    }
    if (key === 'km') {
      const dateLabel = formatKmDate(r.km_date);
      return dateLabel ? `${dateLabel}\n${v} km` : `${v} km`;
    }
    return String(v);
  };

  const filenameSuffix = `${new Date().toISOString().slice(0, 10)}`;

  const exportPDF = () => {
    if (exportRows.length === 0) return toast.error('No hay bicicletas para exportar');
    const cols = buildColumns();
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    doc.setFontSize(14);
    doc.text('Lista de Bicicletas', 14, 14);
    doc.setFontSize(9);
    doc.setTextColor(100);
    const selNote = selectedIds.size > 0 ? ' · Selección manual' : '';
    doc.text(`${activeEmpresaNombre || ''} · ${exportRows.length} bicicleta${exportRows.length !== 1 ? 's' : ''}${selNote} · ${new Date().toLocaleDateString('es-ES')}`, 14, 20);

    let startY = 25;
    exportGroups.forEach(g => {
      if (g.labels.length > 0) {
        const crumb = g.labels.map(l => `${GROUPER_LABEL[l.key]}: ${l.value}`).join(' · ');
        doc.setFontSize(10);
        doc.setTextColor(15, 23, 42);
        doc.text(`${crumb}  (${g.rows.length})`, 14, startY + 2);
        startY += 5;
      }
      autoTable(doc, {
        startY,
        head: [cols.map(c => c.label)],
        body: g.rows.map(r => cols.map(c => formatCell(r, c.key))),
        styles: { fontSize: 8, cellPadding: 1.5 },
        headStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [250, 250, 252] },
        margin: { left: 10, right: 10 },
      });
      startY = doc.lastAutoTable.finalY + 6;
    });
    doc.save(`lista_bicicletas_${filenameSuffix}.pdf`);
    toast.success('PDF exportado');
  };

  const exportExcel = () => {
    if (exportRows.length === 0) return toast.error('No hay bicicletas para exportar');
    const cols = buildColumns();
    const data = [cols.map(c => c.label)];
    exportGroups.forEach(g => {
      if (g.labels.length > 0) {
        const crumb = g.labels.map(l => `${GROUPER_LABEL[l.key]}: ${l.value}`).join(' · ');
        data.push([`${crumb} (${g.rows.length})`]);
      }
      g.rows.forEach(r => data.push(cols.map(c => formatCell(r, c.key))));
    });
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = cols.map(c => ({ wch: c.key === 'model' ? 28 : 16 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Lista');
    XLSX.writeFile(wb, `lista_bicicletas_${filenameSuffix}.xlsx`);
    toast.success('Excel exportado');
  };

  const doPrint = () => {
    if (exportRows.length === 0) return toast.error('No hay bicicletas para imprimir');
    const cols = buildColumns();
    const win = window.open('', '_blank', 'width=1000,height=700');
    if (!win) return toast.error('Bloqueador de ventanas activo');
    const headRow = cols.map(c => `<th>${c.label}</th>`).join('');
    const blocks = exportGroups.map(g => {
      const groupHead = g.labels.length > 0
        ? `<h2>${g.labels.map(l => `${GROUPER_LABEL[l.key]}: ${l.value}`).join(' · ')} <span class="cnt">(${g.rows.length})</span></h2>`
        : '';
      const body = g.rows.map(r =>
        `<tr>${cols.map(c => `<td>${formatCell(r, c.key)}</td>`).join('')}</tr>`
      ).join('');
      return `${groupHead}<table><thead><tr>${headRow}</tr></thead><tbody>${body}</tbody></table>`;
    }).join('');
    const selNote = selectedIds.size > 0 ? ' · Selección manual' : '';
    win.document.write(`<!doctype html><html><head><meta charset="utf-8"/>
      <title>Lista de Bicicletas</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif; color: #0f172a; margin: 0; padding: 8mm; }
        h1 { font-size: 16pt; margin: 0 0 1mm 0; }
        h2 { font-size: 11pt; margin: 6mm 0 2mm 0; color: #334155; }
        h2 .cnt { color: #64748b; font-weight: 400; font-size: 9pt; }
        .sub { font-size: 9pt; color: #64748b; margin-bottom: 4mm; }
        table { width: 100%; border-collapse: collapse; font-size: 9pt; margin-bottom: 4mm; }
        th, td { border: 1px solid #e2e8f0; padding: 3px 6px; text-align: left; vertical-align: middle; }
        thead { background: #f1f5f9; }
        tbody tr:nth-child(even) { background: #fafafa; }
        @media print { body { padding: 0; } }
      </style></head><body>
        <h1>Lista de Bicicletas</h1>
        <div class="sub">${activeEmpresaNombre || ''} · ${exportRows.length} bicicleta${exportRows.length !== 1 ? 's' : ''}${selNote} · ${new Date().toLocaleDateString('es-ES')}</div>
        ${blocks}
        <script>window.onload = function(){ setTimeout(function(){ window.print(); window.close(); }, 250); }<\/script>
      </body></html>`);
    win.document.close();
  };

  const clearFilters = () => {
    setSearch(''); setModelFilters(new Set()); setStatusFilter('all');
    setSortKey('number'); setSortDir('asc'); setGroupKeys([]);
  };

  const toggleGrouper = (key) => {
    setGroupKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };
  const clearGroupers = () => setGroupKeys([]);

  const cols = buildColumns();

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-6xl max-h-[92vh] flex flex-col p-0 gap-0" data-testid="export-bicycles-modal">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-200">
          <DialogTitle className="font-['Barlow_Condensed'] text-2xl">Lista de Bicicletas</DialogTitle>
          <DialogDescription>
            Vista de gestión de flota con filtros, agrupación y exportación. Solo se listan bicicletas activas, en taller, en revisión, rotas o prestadas.
          </DialogDescription>
        </DialogHeader>

        {/* Filters toolbar */}
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50/60 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px] max-w-md">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Buscar</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Nº, modelo, talla, color, bastidor..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-9"
                  data-testid="lista-search-input"
                />
              </div>
            </div>

            <div className="min-w-[180px]">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Modelos</label>
              <Popover open={modelPopoverOpen} onOpenChange={setModelPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="h-9 w-full justify-between font-normal"
                    data-testid="lista-model-filter"
                  >
                    <span className="truncate text-sm">
                      {modelFilters.size === 0
                        ? 'Todos los modelos'
                        : modelFilters.size === 1
                          ? [...modelFilters][0]
                          : `${modelFilters.size} modelos`}
                    </span>
                    <ChevronDown size={14} className="text-slate-400 flex-shrink-0 ml-1" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0" align="start">
                  <div className="p-2 border-b border-slate-200">
                    <div className="relative">
                      <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        autoFocus
                        placeholder="Buscar modelo..."
                        value={modelSearch}
                        onChange={(e) => setModelSearch(e.target.value)}
                        className="pl-7 h-8 text-sm"
                        data-testid="model-popover-search"
                      />
                    </div>
                    <div className="flex items-center justify-between mt-2 text-xs">
                      <button
                        type="button"
                        className="text-orange-600 hover:text-orange-700 font-medium"
                        onClick={() => setModelFilters(new Set(allModels))}
                        data-testid="model-popover-select-all"
                      >Seleccionar todos</button>
                      <button
                        type="button"
                        className="text-slate-500 hover:text-slate-700"
                        onClick={() => setModelFilters(new Set())}
                        data-testid="model-popover-clear"
                      >Limpiar</button>
                    </div>
                  </div>
                  <div
                    className="max-h-64 overflow-y-auto overscroll-contain scroll-smooth"
                    data-testid="model-popover-scroll"
                  >
                    <div className="p-1">
                      {allModels
                        .filter(m => !modelSearch || m.toLowerCase().includes(modelSearch.toLowerCase()))
                        .map(m => {
                          const checked = modelFilters.has(m);
                          return (
                            <label
                              key={m}
                              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm"
                              data-testid={`model-popover-item-${m}`}
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={() => {
                                  setModelFilters(prev => {
                                    const next = new Set(prev);
                                    if (next.has(m)) next.delete(m); else next.add(m);
                                    return next;
                                  });
                                }}
                                data-testid={`model-popover-checkbox-${m}`}
                              />
                              <span className="truncate">{m}</span>
                            </label>
                          );
                        })}
                      {allModels.filter(m => !modelSearch || m.toLowerCase().includes(modelSearch.toLowerCase())).length === 0 && (
                        <div className="text-center text-xs text-slate-400 py-3">Sin coincidencias</div>
                      )}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <div className="min-w-[140px]">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Estado</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9" data-testid="lista-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {availableStatuses.map(s => (
                    <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[160px]">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Ordenar por</label>
              <Select value={sortKey} onValueChange={setSortKey}>
                <SelectTrigger className="h-9" data-testid="lista-sort-by"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map(o => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <Button
              variant="outline"
              className="h-9 gap-2"
              onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
              data-testid="lista-sort-direction"
            >
              {sortDir === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
              {sortDir === 'asc' ? 'Asc' : 'Desc'}
            </Button>

            {(search || modelFilters.size > 0 || statusFilter !== 'all' || groupKeys.length > 0) && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 gap-1 text-slate-500" data-testid="lista-clear-filters">
                <X size={14} /> Quitar filtros
              </Button>
            )}
          </div>

          {/* Multi-grouper toggles */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              <Layers size={11} /> Agrupar por
            </div>
            {GROUPERS.map(g => {
              const active = groupKeys.includes(g.key);
              const idx = active ? groupKeys.indexOf(g.key) + 1 : null;
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => toggleGrouper(g.key)}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors flex items-center gap-1 ${
                    active
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                  }`}
                  data-testid={`grouper-${g.key}`}
                >
                  {active && <span className="bg-white/30 text-white rounded-full w-4 h-4 inline-flex items-center justify-center text-[10px] font-bold">{idx}</span>}
                  {g.label}
                </button>
              );
            })}
            {groupKeys.length > 0 && (
              <button
                type="button"
                onClick={clearGroupers}
                className="text-xs text-slate-500 hover:text-slate-700 underline ml-1"
                data-testid="clear-groupers"
              >
                Limpiar agrupadores
              </button>
            )}
            {groupKeys.length > 1 && (
              <span className="text-xs text-slate-400 ml-2">
                Orden de agrupación: {groupKeys.map(k => GROUPER_LABEL[k]).join(' → ')}
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <div className="flex items-center gap-2">
              <ListFilter size={12} />
              <span><strong className="text-slate-700">{sortedRows.length}</strong> de <strong>{baseRows.length}</strong> bicicletas listables</span>
              {modelFilters.size > 0 && (
                <Badge variant="outline" className="ml-1">
                  Modelos ({modelFilters.size}): {[...modelFilters].slice(0, 2).join(', ')}{modelFilters.size > 2 ? ` +${modelFilters.size - 2}` : ''}
                </Badge>
              )}
              {statusFilter !== 'all' && <Badge variant="outline" className="ml-1">Estado: {STATUS_LABEL[statusFilter]}</Badge>}
              {groupKeys.length > 0 && <Badge variant="outline" className="ml-1">Agrupado: {groupKeys.map(k => GROUPER_LABEL[k]).join(' › ')}</Badge>}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={exportExcel} className="h-8 gap-1" data-testid="export-excel-btn">
                <FileSpreadsheet size={14} /> Excel
              </Button>
              <Button variant="outline" size="sm" onClick={exportPDF} className="h-8 gap-1" data-testid="export-pdf-btn">
                <FileText size={14} /> PDF
              </Button>
              <Button size="sm" onClick={doPrint} className="h-8 bg-orange-500 hover:bg-orange-600 text-white gap-1" data-testid="print-list-btn">
                <Printer size={14} /> Imprimir
              </Button>
            </div>
          </div>

          {/* Manual selection toolbar */}
          <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-200/60">
            <div className="flex items-center gap-1.5 text-xs text-slate-600">
              <CheckSquare size={12} className={selectedIds.size > 0 ? 'text-orange-600' : 'text-slate-400'} />
              <span>
                <strong className={selectedIds.size > 0 ? 'text-orange-600' : 'text-slate-700'}>{selectedIds.size}</strong> seleccionada{selectedIds.size !== 1 ? 's' : ''}
                {selectedIds.size > 0 && (
                  <span className="text-slate-400 ml-1">· Las exportaciones usan sólo la selección</span>
                )}
              </span>
            </div>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={selectAllVisible} data-testid="select-all-visible-btn">
              Seleccionar todas (visibles)
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500" onClick={clearAllSelection} data-testid="clear-all-selection-btn" disabled={selectedIds.size === 0}>
              Deseleccionar todas
            </Button>
          </div>
        </div>

        {/* Table — uses native overflow-y-auto for reliable nested scroll; min-h-0 lets flex-1 shrink */}
        <div
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain scroll-smooth px-6 py-3"
          data-testid="lista-scroll-container"
        >
          {sortedRows.length === 0 ? (
            <div className="text-center text-slate-400 py-16">
              <ListFilter size={32} className="mx-auto mb-2 text-slate-300" />
              <p>No hay bicicletas que coincidan con los filtros.</p>
            </div>
          ) : (
            groups.map((g, idx) => {
              const groupKey = g.labels.map(l => `${l.key}=${l.value}`).join('|') || 'all';
              return (
              <div key={groupKey} className={idx > 0 ? 'mt-5' : ''} data-testid={g.labels.length > 0 ? `group-block-${groupKey}` : 'all-rows-block'}>
                {g.labels.length > 0 && (
                  <div className="flex items-baseline gap-2 mb-2 px-1 flex-wrap">
                    {g.labels.map((l, li) => (
                      <span key={li} className="flex items-baseline gap-1">
                        {li > 0 && <span className="text-slate-300">›</span>}
                        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{GROUPER_LABEL[l.key]}:</span>
                        <span className="text-sm font-semibold text-slate-700">{l.value}</span>
                      </span>
                    ))}
                    <Badge variant="outline" className="text-xs ml-1">{g.rows.length}</Badge>
                  </div>
                )}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 hover:bg-slate-50">
                        <TableHead className="w-10">
                          <Checkbox
                            checked={headerCheckState}
                            onCheckedChange={toggleHeaderCheckbox}
                            data-testid="header-select-checkbox"
                          />
                        </TableHead>
                        {cols.map(c => (
                          <TableHead key={c.key} className="text-xs font-semibold uppercase tracking-wider">
                            {c.label}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {g.rows.map(r => {
                        const checked = selectedIds.has(r.id);
                        return (
                          <TableRow
                            key={r.id}
                            className={`hover:bg-slate-50 ${checked ? 'bg-orange-50/40' : ''}`}
                            data-testid={`lista-row-${r.id}`}
                          >
                            <TableCell className="w-10">
                              <Checkbox
                                checked={checked}
                                onCheckedChange={() => toggleRowSelect(r.id)}
                                data-testid={`row-checkbox-${r.id}`}
                              />
                            </TableCell>
                            {cols.map(c => {
                              if (c.key === 'number') {
                                return <TableCell key={c.key} className="font-mono font-semibold text-slate-800">{r.number || '-'}</TableCell>;
                              }
                              if (c.key === 'statusLabel') {
                                return (
                                  <TableCell key={c.key}>
                                    <Badge className={`text-xs ${STATUS_COLOR[r.status] || 'bg-slate-100 text-slate-700'}`}>
                                      {r.statusLabel || '-'}
                                    </Badge>
                                  </TableCell>
                                );
                              }
                              if (c.key === 'frame_number') {
                                return <TableCell key={c.key} className="font-mono text-sm">{r.frame_number || '-'}</TableCell>;
                              }
                              if (c.key === 'km') {
                                const kmVal = r.km !== '' && r.km != null ? r.km : '';
                                const dateLabel = formatKmDate(r.km_date);
                                return (
                                  <TableCell key={c.key} className="leading-tight" data-testid={`km-cell-${r.id}`}>
                                    {dateLabel && (
                                      <div className="text-[10px] text-slate-400 font-normal" data-testid={`km-date-${r.id}`}>
                                        {dateLabel}
                                      </div>
                                    )}
                                    <div className="text-sm">{kmVal !== '' ? `${kmVal} km` : '-'}</div>
                                  </TableCell>
                                );
                              }
                              return <TableCell key={c.key}>{r[c.key] !== '' && r[c.key] != null ? r[c.key] : '-'}</TableCell>;
                            })}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
